import { post } from '@/helpers/api'
import { get } from '@/api/rest'
import { imageResizePresets } from '@/helpers/constants'
import { setUpload } from '@/store'
import axios from 'axios'
import { captureLog } from '@/helpers/log'
import { setMediaUpload } from '@/store/uploads'

const log = (...args) => captureLog('UPLOAD', ...args)

export const getFileUploadKeys = async (count = 1) => {
	try {
		const { data } = await post('/api/v1/upload.getfileuploadkeys', {
			count: count,
		})
		return data
	} catch (e) {
		console.error(e)
		const { error } = (e.response && e.response.data) || {}
		return { ok: false, error }
	}
}

export const uploadFileToS3 = async (file, url, onUploadProgress) => {
	try {
		var options = {
			headers: {
				'Content-Type': file.type,
			},
			onUploadProgress: onUploadProgress,
		}

		const data = await axios.put(url, file, options)
		return data
	} catch (e) {
		console.error('error', e)
	}
}

export const uploadNew = async (file, onProgress, cancelToken) => {
	const onUploadProgress = ({ loaded, total }) => {
		const percent = Math.round((loaded / total) * 100)
		log('progress', `${percent}%`, { percent, loaded, total })
		if (onProgress) {
			onProgress(percent, loaded, total)
		}
	}
	try {
		const filename = encodeURIComponent(file.name)
		const url = `${window.location.origin}/api/files?filename=${filename}`
		const headers = {}
		if (file.type) {
			headers['Content-Type'] = file.type
		}
		log('sending file to', url, headers)
		const { data } = await axios.put(url, file, {
			headers,
			onUploadProgress,
			cancelToken,
		})
		log('upload complete', data)
		return data
	} catch (e) {
		if (!axios.isCancel(e)) {
			console.error('error', e)
			window.prontoAlert.error(e)
		}
	}
}

export const uploadDirect = async (file, options = {}) => {
	const { onProgress, cancelToken, blob, uuid } = options
	if (uuid) {
		setUpload(uuid, { file, blob })
	}
	const response = await uploadNew(file, onProgress, cancelToken)
	return response
}

export const uploadMedia = async (media, options = {}) => {
	const { onProgress, cancelToken } = options
	if (media.tempId) {
		setMediaUpload(media)
	}
	const response = await uploadNew(media.file, onProgress, cancelToken)
	return response
}

export const isImageFile = (file) => {
	if (!file) return false
	return file.type?.startsWith('image/') ?? false
}

export const normalizeImageForFileKey = async (
	key,
	preset = imageResizePresets.PHOTO
) => {
	if (!key || !preset)
		throw new Error('Missing required field(s) for normalization')
	try {
		log('preparing to normalize...', key, preset)
		const { data } = await get(`/files/${key}/normalized`, { preset })
		log('normalization complete', data)
		return data
	} catch (e) {
		log('ERROR during normalization', e.message)
		if (!axios.isCancel(e)) {
			console.error('Image failed to normalize', e)
		}
		throw e
	}
}

export const transcodeVideoForFileKey = async (fileUploadKey) => {
	if (!fileUploadKey) throw new Error('Missing filekey for transcoding')
	try {
		log('sending transcode request...', fileUploadKey)
		const resp = await post('/api/clients/transcode_jobs', {
			fileUploadKey,
		})
		log('transode request complete', resp.data.data)
		return resp.data.data
	} catch (e) {
		log('ERROR during transcoding request', e.message)
		if (!axios.isCancel(e)) {
			console.error('Video failed to transcode', e)
		}
		throw e
	}
}

export const getTranscodeJobStatus = async (jobId) => {
	if (!jobId) throw new Error('Missing job id')
	try {
		log('fetching transcode job details...', jobId)
		const resp = await get(`/transcode_jobs/${jobId}`)
		log('transode job request complete', resp.data.data)
		return resp.data
	} catch (e) {
		log('ERROR fetching transcode job status', e.message)
		if (!axios.isCancel(e)) {
			console.error('Video failed to transcode', e)
		}
		throw e
	}
}
